/**
 * Authentication Storage Utility
 *
 * Manages secure storage of authentication tokens in localStorage
 * Based on data-model.md storage schema
 */

import type { AuthSession, UserInfo } from "@/types/auth";

/**
 * localStorage keys for authentication data
 * All keys prefixed with 'auth_' for namespace isolation
 */
export const STORAGE_KEYS = {
  ACCESS_TOKEN: "auth_access_token",
  CSRF_TOKEN: "auth_csrf_token",
  ID_TOKEN: "auth_id_token",
  TOKEN_EXPIRY: "auth_token_expiry", // stored as string timestamp
  USER_INFO: "auth_user_info", // stored as JSON string
  ROLE_ID: "auth_role_id",
  CLIENT_ID: "auth_client_id", // always "liff-app"
} as const;

/**
 * Refresh window for proactive token refresh (5 minutes in milliseconds)
 * Token is considered expired if less than 5 minutes remaining
 */
export const REFRESH_WINDOW_MS = 5 * 60 * 1000;

/**
 * Check if a token is expired based on its expiry timestamp
 *
 * @param expiryTimestamp - Unix timestamp in milliseconds
 * @returns true if token is expired or expiring within refresh window
 */
export function isTokenExpired(expiryTimestamp: number): boolean {
  // Consider token expired if less than 5 minutes remaining
  return Date.now() > expiryTimestamp - REFRESH_WINDOW_MS;
}

/**
 * Validate JWT format (3 parts separated by dots)
 *
 * @param token - JWT token string
 * @returns true if token has valid JWT format
 */
export function isValidJWT(token: string): boolean {
  // JWT must have 3 parts: header.payload.signature
  const parts = token.split(".");
  return parts.length === 3 && parts.every((part) => part.length > 0);
}

/**
 * Validate stored session integrity
 * Checks that all required keys exist and have valid values
 *
 * @returns true if session is valid, false otherwise
 */
export function validateStoredSession(): boolean {
  const requiredKeys = [
    STORAGE_KEYS.ACCESS_TOKEN,
    STORAGE_KEYS.CSRF_TOKEN,
    STORAGE_KEYS.ID_TOKEN,
    STORAGE_KEYS.TOKEN_EXPIRY,
    STORAGE_KEYS.USER_INFO,
    STORAGE_KEYS.ROLE_ID,
    STORAGE_KEYS.CLIENT_ID,
  ];

  // All keys must exist
  const allExist = requiredKeys.every(
    (key) => localStorage.getItem(key) !== null,
  );
  if (!allExist) return false;

  // Access token must be valid JWT
  const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)!;
  if (!isValidJWT(accessToken)) return false;

  // ID token must be valid JWT
  const idToken = localStorage.getItem(STORAGE_KEYS.ID_TOKEN)!;
  if (!isValidJWT(idToken)) return false;

  // Expiry must be future timestamp
  const expiryStr = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY)!;
  const expiry = parseInt(expiryStr, 10);
  if (isNaN(expiry) || expiry < Date.now()) return false;

  // User info must be valid JSON
  try {
    const userInfoStr = localStorage.getItem(STORAGE_KEYS.USER_INFO)!;
    const userInfo = JSON.parse(userInfoStr) as UserInfo;
    if (!userInfo.id || !userInfo.line_user_id) return false;
  } catch {
    return false;
  }

  // Client ID must be "liff-app"
  const clientId = localStorage.getItem(STORAGE_KEYS.CLIENT_ID)!;
  if (clientId !== "liff-app") return false;

  return true;
}

/**
 * Save authentication session to localStorage
 *
 * @param session - Complete authentication session data
 * @throws Error if session data is invalid
 */
export function saveAuthSession(session: AuthSession): void {
  // Validate session before saving
  if (!isValidJWT(session.accessToken)) {
    throw new Error("Invalid access token format");
  }

  if (!isValidJWT(session.idToken)) {
    throw new Error("Invalid ID token format");
  }

  if (session.tokenExpiry < Date.now()) {
    throw new Error("Token expiry is in the past");
  }

  if (!session.user.id || !session.user.line_user_id) {
    throw new Error("User info is incomplete");
  }

  if (session.clientId !== "liff-app") {
    throw new Error("Invalid client ID");
  }

  // Save all fields to localStorage
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, session.accessToken);
  localStorage.setItem(STORAGE_KEYS.CSRF_TOKEN, session.csrfToken);
  localStorage.setItem(STORAGE_KEYS.ID_TOKEN, session.idToken);
  localStorage.setItem(
    STORAGE_KEYS.TOKEN_EXPIRY,
    session.tokenExpiry.toString(),
  );
  localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(session.user));
  localStorage.setItem(STORAGE_KEYS.ROLE_ID, session.roleId);
  localStorage.setItem(STORAGE_KEYS.CLIENT_ID, session.clientId);
}

/**
 * Load authentication session from localStorage
 *
 * @returns AuthSession if valid session exists, null otherwise
 */
export function loadAuthSession(): AuthSession | null {
  // Validate session before loading
  if (!validateStoredSession()) {
    // Clear invalid session
    clearAuthSession();
    return null;
  }

  try {
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)!;
    const csrfToken = localStorage.getItem(STORAGE_KEYS.CSRF_TOKEN)!;
    const idToken = localStorage.getItem(STORAGE_KEYS.ID_TOKEN)!;
    const tokenExpiry = parseInt(
      localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY)!,
      10,
    );
    const user = JSON.parse(
      localStorage.getItem(STORAGE_KEYS.USER_INFO)!,
    ) as UserInfo;
    const roleId = localStorage.getItem(STORAGE_KEYS.ROLE_ID)!;
    const clientId = localStorage.getItem(STORAGE_KEYS.CLIENT_ID)!;

    return {
      accessToken,
      csrfToken,
      idToken,
      tokenExpiry,
      user,
      roleId,
      clientId,
    };
  } catch {
    // If parsing fails, clear session
    clearAuthSession();
    return null;
  }
}

/**
 * Clear all authentication data from localStorage
 * Called on logout or authentication failure
 */
export function clearAuthSession(): void {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.CSRF_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
  localStorage.removeItem(STORAGE_KEYS.USER_INFO);
  localStorage.removeItem(STORAGE_KEYS.ROLE_ID);
  localStorage.removeItem(STORAGE_KEYS.CLIENT_ID);
}

/**
 * Get current access token from localStorage
 *
 * @returns access token if exists and not expired, null otherwise
 */
export function getAccessToken(): string | null {
  const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (!accessToken) return null;

  // Check if token is expired
  const expiryStr = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
  if (!expiryStr) return null;

  const expiry = parseInt(expiryStr, 10);
  if (isNaN(expiry) || isTokenExpired(expiry)) {
    return null;
  }

  return accessToken;
}

/**
 * Get CSRF token from localStorage
 *
 * @returns CSRF token if exists, null otherwise
 */
export function getCsrfToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.CSRF_TOKEN);
}

/**
 * Get token expiry timestamp from localStorage
 *
 * @returns expiry timestamp in milliseconds, null if not found
 */
export function getTokenExpiry(): number | null {
  const expiryStr = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
  if (!expiryStr) return null;

  const expiry = parseInt(expiryStr, 10);
  return isNaN(expiry) ? null : expiry;
}

/**
 * Get current role ID from localStorage
 *
 * @returns role ID if exists, null otherwise
 */
export function getRoleId(): string | null {
  return localStorage.getItem(STORAGE_KEYS.ROLE_ID);
}

/**
 * Update access token and expiry (used after token refresh)
 *
 * @param accessToken - New access token
 * @param expiresIn - Token expiration in seconds
 */
export function updateAccessToken(
  accessToken: string,
  expiresIn: number,
): void {
  if (!isValidJWT(accessToken)) {
    throw new Error("Invalid access token format");
  }

  const tokenExpiry = Date.now() + expiresIn * 1000;
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, tokenExpiry.toString());
}

/**
 * Update ID token (used after token refresh)
 *
 * @param idToken - New ID token
 */
export function updateIdToken(idToken: string): void {
  if (!isValidJWT(idToken)) {
    throw new Error("Invalid ID token format");
  }

  localStorage.setItem(STORAGE_KEYS.ID_TOKEN, idToken);
}

/**
 * Update CSRF token (used after token refresh if rotating)
 *
 * @param csrfToken - New CSRF token
 */
export function updateCsrfToken(csrfToken: string): void {
  localStorage.setItem(STORAGE_KEYS.CSRF_TOKEN, csrfToken);
}

/**
 * Get user ID from localStorage
 *
 * Convenience function for accessing just the user ID without loading full session
 * Useful for API endpoint construction: `/users/${userId}/reload_tokens`
 *
 * @returns User ID if exists and session is valid, null otherwise
 *
 * @example
 * ```typescript
 * const userId = getUserId();
 * if (userId) {
 *   const tokens = await apiClient.get(`/users/${userId}/reload_tokens`);
 * }
 * ```
 */
export function getUserId(): string | null {
  try {
    const userInfoStr = localStorage.getItem(STORAGE_KEYS.USER_INFO);
    if (!userInfoStr) return null;

    const userInfo = JSON.parse(userInfoStr) as UserInfo;
    return userInfo.id || null;
  } catch {
    return null;
  }
}
